import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Subject, catchError, finalize, from, map, mergeMap, of, reduce, switchMap, takeUntil } from 'rxjs';
import { Transaction } from '@contracts/interfaces/transaction.interfaces';
import { InvoiceInterface } from '@contracts/interfaces/invoice.interface';
import { ACCOUNT } from '../../accounts';
import { TransactionsService } from '../../services/transactions.service';
import { InvoicesService } from '../../services/invoices.service';


interface CashInvoiceEntry { payment: Transaction['cashDeposit'][number]; invoiceNumbers: number[]; }

@Component({
  selector: 'app-cash-reconciliation',
  templateUrl: './cash-reconciliation.component.html',
  styleUrls: ['./cash-reconciliation.component.scss']
})
export class CashReconciliationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new MatTableDataSource<Transaction>([]);
  columns = ['date', 'description', 'amount', 'sorted', 'note'];
  loading = false;
  error = '';
  search = '';
  startDate = '';
  endDate = '';
  lastLoaded: Date | null = null;
  cashDetails = new Map<Transaction, CashInvoiceEntry[]>();
  invoicesByNumber = new Map<number, InvoiceInterface>();
  invoiceError = '';
  readonly hasCashDetails = (_: number, row: Transaction) => (this.cashDetails.get(row)?.length || 0) > 0;
  readonly trackCashEntry = (index: number, entry: CashInvoiceEntry) =>
    `${entry.payment.invoice || ''}:${index}`;
  readonly trackInvoiceNumber = (_: number, number: number) => number;
  private destroyed = new Subject<void>();

  constructor(private transactions: TransactionsService, private invoices: InvoicesService) {
    this.dataSource.sortingDataAccessor = (row, column) => column === 'date' ? new Date(row.date).getTime() :
      column === 'amount' ? row.amount : column === 'sorted' ? Number(row.sorted === true) : String(row[column] || '').toLowerCase();
    this.dataSource.filterPredicate = row => {
      const text = `${row.description || ''} ${row.note || ''} ${(row.cashDeposit || []).map(payment => payment.invoice || '').join(' ')}`.toLowerCase();
      const day = this.localDay(row.date);
      return (!this.search.trim() || text.includes(this.search.trim().toLowerCase())) &&
        (!this.startDate || day >= this.startDate) && (!this.endDate || day <= this.endDate);
    };
  }
  ngOnInit(): void { this.load(); }
  ngAfterViewInit(): void { this.dataSource.paginator = this.paginator; this.dataSource.sort = this.sort; }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }
  get total(): number { return this.dataSource.filteredData.reduce((sum, row) => sum + Number(row.amount || 0), 0); }
  get dateError(): boolean { return !!(this.startDate && this.endDate && this.startDate > this.endDate); }
  load(): void {
    if (this.loading) return;
    this.loading = true; this.error = '';
    // Pending Revenue account 078086297. Include all deposits regardless of
    // bookkeeping sorted/ignored flags.
    this.transactions.find({ 'source.account': ACCOUNT.G72REVENUE,
      description: { $regex: 'Apost', $options: 'i' }, amount: { $gt: 0 }
    }, 0, 0, { date: -1, 'source.sha1': 1 }).pipe(
      // Fetch the complete replacement in the background. Keep existing rows and
      // invoice links visible until both requests have settled.
      switchMap(rows => this.loadInvoices(rows).pipe(
        map(invoices => ({ rows, invoices, invoiceError: '' })),
        catchError(() => of({ rows, invoices: null, invoiceError: 'Invoice details could not be loaded. Previously loaded details are retained; saved Cash references and amounts are still shown.' }))
      )),
      takeUntil(this.destroyed), finalize(() => this.loading = false)
    ).subscribe({
      next: ({ rows, invoices, invoiceError }) => {
        const existing = new Map(this.dataSource.data.filter(row => row.source?.sha1).map(row => [row.source.sha1, row]));
        const updated = rows.map(row => {
          const previous = existing.get(row.source?.sha1);
          if (!previous) return row;
          // Preserve object identity so Material retains both deposit/detail row
          // elements, keyboard focus and the browser's existing scroll anchor.
          for (const key of Object.keys(previous)) if (!(key in row)) delete previous[key];
          return Object.assign(previous, row);
        });
        const details = new Map<Transaction, CashInvoiceEntry[]>();
        for (const row of updated) details.set(row, (row.cashDeposit || [])
          .map(payment => ({ payment, invoiceNumbers: this.invoiceNumbers(payment.invoice) })));
        this.cashDetails = details;
        if (invoices !== null) this.invoicesByNumber = new Map(invoices.map(invoice => [invoice.number, invoice]));
        this.invoiceError = invoiceError;
        // Replacing data reapplies the current filters/sort without firstPage().
        this.dataSource.data = updated; this.lastLoaded = new Date();
      },
      error: error => { this.error = error.error?.error || 'Unable to refresh Cash deposits. Previously loaded results are still shown.'; }
    });
  }
  applyFilters(): void { this.dataSource.filter = JSON.stringify([this.search, this.startDate, this.endDate]); this.paginator?.firstPage(); }
  clearFilters(): void { this.search = ''; this.startDate = ''; this.endDate = ''; this.applyFilters(); }
  open(row: Transaction): void {
    if (!row.source?.sha1) return;
    this.transactions.openCashRevenueDialog(row, () => this.load());
  }
  openInvoice(invoice: InvoiceInterface): void { this.invoices.openInvoiceDialog(invoice, () => this.load()); }
  private loadInvoices(rows: Transaction[]) {
    const numbers = [...new Set(rows.flatMap(row => (row.cashDeposit || []).flatMap(payment => this.invoiceNumbers(payment.invoice))))];
    if (!numbers.length) return of([] as InvoiceInterface[]);
    const batches: number[][] = [];
    for (let index = 0; index < numbers.length; index += 100) batches.push(numbers.slice(index, index + 100));
    return from(batches).pipe(
      mergeMap(batch => this.invoices.find({ number: { $in: batch } }), 2),
      reduce((all, batch) => all.concat(batch), [] as InvoiceInterface[])
    );
  }
  private invoiceNumbers(reference: string): number[] {
    return [...new Set((reference || '').match(/\d+/g) || [])].map(Number).filter(number => Number.isSafeInteger(number) && number > 0);
  }
  private localDay(value: Date | string): string {
    const date = new Date(value);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
